import { toggleLoaderInButton } from './loader';
import { eventBus } from './event-bus';
import type { UIIcon } from '../components/icon/ui-icon';
import type { UICaption } from '../components/caption/ui-caption';
import type { UIField } from '../components/field/ui-field';
import { UIIconColors } from '../components/icon/ui-icon.helpers';

type LockFormControl =
    | HTMLButtonElement
    | HTMLInputElement
    | HTMLSelectElement
    | UICaption;

/**
 * Locks the form or control due when making HTTP request.
 * @memberof UI
 * @alias runWithLockingForm
 * @param {PromiseLike<unknown>} request
 * @param {HTMLInputElement|HTMLSelectElement|HTMLButtonElement|UICaption} control
 * @param {HTMLElement} context
 * @returns {Promise<Array<Awaited<unknown>> | unknown>}
 */
const runWithLockingForm = (
    request: PromiseLike<unknown>,
    control: LockFormControl,
    context: HTMLElement
): PromiseLike<Array<Awaited<unknown>>> => {
    const beforeActions = [
        (): void => eventBus.dispatchCustomEvent('loading-start'),
    ];
    const afterActions = [
        (): void => eventBus.dispatchCustomEvent('loading-complete'),
    ];

    const updateButtonIcon = () => {
        const icon = control.querySelector<UIIcon>('ui-icon');
        if (!icon) {
            return;
        }
        const prevGlyph = icon.glyph;
        const prevBgColor = icon.bgcolor || UIIconColors.DEFAULT;
        beforeActions.push((): void => {
            icon.glyph = 'loader';
            icon.bgcolor = 'info-gray';
        });
        afterActions.push((): void => {
            icon.glyph = prevGlyph;
            icon.bgcolor = prevBgColor;
        });
    };
    if (control) {
        switch (control.localName) {
            case 'ui-caption':
                if ('layout' in control && control.layout === 'chip') {
                    updateButtonIcon();
                }
                break;
            case 'button':
                if (control.classList.contains('-iconed')) {
                    updateButtonIcon();
                } else {
                    beforeActions.push(() =>
                        toggleLoaderInButton(control as HTMLButtonElement, true)
                    );
                    afterActions.push(() =>
                        toggleLoaderInButton(
                            control as HTMLButtonElement,
                            false
                        )
                    );
                }
                break;
            case 'input':
            case 'select':
            case 'textarea':
                if ('type' in control) {
                    switch (control.type) {
                        case 'select-one':
                        case 'text':
                            beforeActions.push(() => {
                                if ('readOnly' in control) {
                                    control.readOnly = true;
                                    control.classList.add('-loading');
                                }
                            });
                            afterActions.push(() => {
                                if ('readOnly' in control) {
                                    control.readOnly = false;
                                    control.classList.remove('-loading');
                                }
                            });
                            break;
                    }
                }
                break;
        }
    }

    if (context) {
        const preventer = (e: Event) => e.preventDefault();

        const controls = context.querySelectorAll<
            HTMLInputElement & HTMLSelectElement
        >(
            'input:not([disabled]):not([readonly]),select:not([disabled]):not([readonly])'
        );

        const fields = context.querySelectorAll<UIField>('ui-field');

        beforeActions.push(() => {
            fields.forEach((entry) => {
                entry.style.opacity = '0.7';
                entry.style.transition = 'opacity 0.3s ease-in';
            });
            controls.forEach((entry) => {
                entry.readOnly = true;
            });
            context.style.pointerEvents = 'none';
            context.addEventListener('keydown', preventer);
            context.addEventListener('keyup', preventer);
            context.addEventListener('keypress', preventer);
        });
        afterActions.push(() => {
            fields.forEach((entry) => {
                entry.style.opacity = '1';
                entry.style.transition = '';
            });
            controls.forEach((entry): void => {
                entry.readOnly = false;
            });
            context.style.pointerEvents = '';
            context.removeEventListener('keydown', preventer);
            context.removeEventListener('keyup', preventer);
            context.removeEventListener('keypress', preventer);
        });
    }

    const runBlockers = (): void => {
        beforeActions.forEach((r) => r());
    };
    const stopBlockers = (): void => {
        afterActions.forEach((r) => r());
    };

    return Promise.resolve()
        .then(() => runBlockers())
        .then(() => request)
        .then(
            (result) => {
                stopBlockers();
                return [result];
            },
            (e) => {
                stopBlockers();
                throw e;
            }
        );
};

export { runWithLockingForm };
